import { useCallback, useEffect, useRef, useState } from 'react';
import { useIdleTimer } from 'react-idle-timer';
import { useNavigate } from 'react-router-dom';

import caregiverService, { caregiverSignInPath } from '../../services/caregiverService';

const FIFTEEN_MINUTES = 15 * 60 * 1000;
// The "Do you need more time?" warning opens this long before the idle logout,
// matching the tablet/phone app (14 minutes idle + a 60-second countdown).
const WARNING_MS = 60 * 1000;
// Re-mint the caregiver token at most this often while active. Comfortably
// shorter than the backend token ceiling (CAREGIVER_SESSION_TOKEN_MINUTES, 30m)
// so an active session is always renewed well before its token expires, while
// staying far above per-keystroke cadence so activity bursts cause one call.
const RENEW_THROTTLE_MS = 5 * 60 * 1000;

/**
 * Caregiver inactivity timeout + sliding-session renewal (FECOA-1514).
 *
 * - AC1: logs the caregiver out after 15 minutes with no input or navigation.
 *   At 14 minutes a "Do you need more time?" dialog counts down the last
 *   60 seconds, as the tablet/phone app does; "Continue" restarts the timer.
 * - AC2: any activity (key, click, touch, scroll) resets the idle timer AND,
 *   at most once per RENEW_THROTTLE_MS, re-mints the caregiver token via
 *   `extendSession`. Caregiver tokens have no refresh, so without this an
 *   active caregiver would still be 401'd mid-survey when the per-token ceiling
 *   elapsed. Renewal stops the instant the caregiver goes idle.
 * - AC5: on idle logout the caregiver is sent to /caregiver-login?expired=1,
 *   where a "session expired" banner is shown.
 *
 * Rendered only inside CaregiverSessionGuard, so it runs exclusively for an
 * authenticated caregiver session. This is intentionally separate from the
 * staff/participant IdleTimer, which is coupled to AuthContext (caregivers do
 * not use AuthContext) and is disabled in local development.
 */
const CaregiverIdleTimer = () => {
  const navigate = useNavigate();
  const [warning, setWarning] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(WARNING_MS / 1000);
  // Seed with the mount time: the token is fresh from login/navigation, so the
  // first renewal is throttled until RENEW_THROTTLE_MS of activity has elapsed.
  const lastRenewRef = useRef(Date.now());

  const renew = useCallback(() => {
    lastRenewRef.current = Date.now();
    // Failures (e.g. an already-lapsed token → 401) are handled by the api
    // interceptor, which redirects to the expired-login page.
    caregiverService.extendSession().catch(() => {});
  }, []);

  const handleIdle = useCallback(() => {
    setWarning(false);
    const signInPath = caregiverSignInPath({ expired: true });
    caregiverService.logout();
    navigate(signInPath, { replace: true });
  }, [navigate]);

  const handleAction = useCallback(() => {
    if (Date.now() - lastRenewRef.current < RENEW_THROTTLE_MS) return;
    renew();
  }, [renew]);

  const handlePrompt = useCallback(() => {
    setSecondsLeft(WARNING_MS / 1000);
    setWarning(true);
  }, []);

  // Per-tab timer (no crossTab): caregiver sessions live in sessionStorage, so
  // each tab is its own independent session. While the warning is open, stray
  // activity does not dismiss it; only "Continue" does.
  const { activate, getRemainingTime } = useIdleTimer({
    timeout: FIFTEEN_MINUTES,
    promptBeforeIdle: WARNING_MS,
    onPrompt: handlePrompt,
    onIdle: handleIdle,
    onAction: handleAction,
    debounce: 500,
  });

  useEffect(() => {
    if (!warning) return undefined;
    const interval = setInterval(() => {
      setSecondsLeft(Math.max(0, Math.ceil(getRemainingTime() / 1000)));
    }, 1000);
    return () => clearInterval(interval);
  }, [warning, getRemainingTime]);

  const handleContinue = () => {
    setWarning(false);
    activate();
    renew();
  };

  if (!warning) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="caregiver-idle-title"
      aria-describedby="caregiver-idle-body"
    >
      <div className="w-full max-w-md rounded-xl bg-white dark:bg-slate-900 p-6 shadow-xl">
        <p className="text-xs font-semibold uppercase tracking-widest text-emerald-700 dark:text-emerald-400">
          Session security
        </p>
        <h2 id="caregiver-idle-title" className="mt-2 text-xl font-semibold text-gray-900 dark:text-white">
          Do you need more time?
        </h2>
        <p id="caregiver-idle-body" className="mt-2 text-gray-600 dark:text-gray-300">
          For your privacy, you will be signed out in {secondsLeft} seconds. Answers you have already moved past are saved.
        </p>
        <button
          type="button"
          onClick={handleContinue}
          autoFocus
          className="mt-6 w-full rounded-lg bg-emerald-700 px-4 py-3 font-medium text-white hover:bg-emerald-800 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2"
        >
          Continue
        </button>
      </div>
    </div>
  );
};

export default CaregiverIdleTimer;
